import { ErrorHandler, inject } from '@angular/core';
import { Router } from '@angular/router';
import { signalStore, withMethods } from '@ngrx/signals';
import { Events, withEventHandlers } from '@ngrx/signals/events';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { filter, from, switchMap, take } from 'rxjs';
import { profileLoaded } from 'app/events/profile';
import { LicenseFeatureFlag } from 'app/models/license';
import { ThemeService } from 'app/services/theme/theme.service';
import { FeatureFlagService } from 'app/services/feature-flag.service';
import { VersionIntroService } from 'app/services/version-intro.service';
import { ApplicationStore } from 'app/state-management/application/application.store';
import { environment } from 'environments/environment';

export type VersionIntroStore = InstanceType<typeof VersionIntroStore>;

/**
 * The new version notification: after a release, a snack that leads to the release notes.
 * Root on purpose: it listens for `profileLoaded`, and events are not replayed, so `provideVersionIntro()` creates it
 * before the first load.
 */
export const VersionIntroStore = signalStore(
  { providedIn: 'root' },

  withMethods(() => {
    const router = inject(Router);
    const versionIntroService = inject(VersionIntroService);

    return {
      navigateToReleaseNotes(): void {
        versionIntroService.setVersionIntroCookie();
        router.navigate(['administration', 'release-notes'], { queryParams: { expandLatestRelease: true } });
      },
    };
  }),

  withEventHandlers((store) => {
    const events = inject(Events);
    const errorHandler = inject(ErrorHandler);
    const appStore = inject(ApplicationStore);
    const themeService = inject(ThemeService);
    const featureFlagService = inject(FeatureFlagService);
    const translateService = inject(TranslateService);
    const versionIntroService = inject(VersionIntroService);

    const closeVersionIntro = (): void => versionIntroService.setVersionIntroCookie();

    const showVersionIntroSnack = (): void => appStore.showSnack({
      disappearAfter: 0,
      text: translateService.instant('VERSION_INTRO.SNACKBAR_LABEL'),
      variant: 'c',
      type: 'info',
      showClose: true,
      action: translateService.instant('VERSION_INTRO.ACTION_BUTTON_LABEL'),
      closeOnAction: true,
      actionForOk: store.navigateToReleaseNotes,
      actionForClose: closeVersionIntro,
    });

    return {
      // The cookie "sma-web-version-intro" keeps the last shown version; without it, e.g. on a new browser, the intro is shown.
      // The theme may disable the intro, and the snackbar cannot be taken back, so the flag is read once the theme settles.
      profileLoaded$: events.on(profileLoaded).pipe(
        switchMap(() => themeService.themeSettled$.pipe(
          filter((settled: boolean) => settled),
          take(1),
          switchMap(() => from(featureFlagService.featureOnLocal(LicenseFeatureFlag.VERSION_INTRO))),
          tapResponse({
            next: (showVersionIntro: boolean) => {
              if (!showVersionIntro) {
                closeVersionIntro();
                return;
              }

              const currentVersion: string = environment.version;
              if (currentVersion.split('.').length >= 3 && versionIntroService.shouldShowVersionIntro(currentVersion)) {
                showVersionIntroSnack();
              }
            },
            // no request behind it, so no snack: reported where the NgRx effect it replaces reported
            error: (error: unknown) => errorHandler.handleError(error),
          }),
        )),
      ),
    };
  }),
);
